import { useEffect, useState } from 'react';
import { AuthenticationData } from '@/features/auth/types/Auth.types';
import { useTypedDispatch } from '@/core/redux/hooks';
import { setAuthInfo } from '@/core/features/auth/redux/authorization.reducer';

interface WebViewAuthData
  extends Pick<
    AuthenticationData,
    'accessToken' | 'accessTokenExpireDate' | 'refreshToken' | 'refreshTokenExpireDate'
  > {}

type WebViewStatus = 'finished' | 'loading' | 'idle';

/**
 * Custom hook to handle authentication from React Native WebView
 * Gets auth data from window.IS_RN_WEBVIEW object
 */

export const useWebViewAuth = () => {
  const dispatch = useTypedDispatch();
  const [webViewStatus, setWebViewStatus] = useState<WebViewStatus>('idle');

  useEffect(() => {
    const windowData = (window as any).IS_RN_WEBVIEW;

    // Check if we're in a WebView and if it contains the auth data
    if (!windowData || typeof windowData !== 'object' || !windowData.accessToken) {
      setWebViewStatus('finished');
      return;
    }

    let cycleStatus: WebViewStatus = 'loading';
    setWebViewStatus('loading');

    // Process auth data from window object
    const authData = windowData as WebViewAuthData;

    if (authData) {
      const updatedAuthData: WebViewAuthData = {
        accessToken: authData.accessToken,
        accessTokenExpireDate: new Date(+authData.accessTokenExpireDate).toISOString(),
        refreshToken: authData.refreshToken,
        refreshTokenExpireDate: new Date(+authData.refreshTokenExpireDate).toISOString(),
      };

      // Update Redux state immediately (triggers re-renders)
      dispatch(setAuthInfo(updatedAuthData));

      // Clear the window data after processing
      (window as any).IS_RN_WEBVIEW = true; // Reset to just boolean flag

      cycleStatus = 'finished';
      setWebViewStatus('finished');
    }

    // Terminate operation in case it takes too long
    setTimeout(() => {
      if (cycleStatus === 'loading') {
        setWebViewStatus('finished');
        cycleStatus = 'finished';
      }
    }, 8000);

    return () => {};
  }, [dispatch]);

  return webViewStatus;
};
